import { DateTimeHelper } from 'src/app/core/helper/datetime.helper';
import { RaffleDrawType, RaffleStatus, RaffleType } from './raffle-rocket.enums';
import { PricingTier } from './pricing-tier';
import { Prize } from './prize';
import { RaffleWinner } from './raffle-winner';
import { Sponsor } from './sponsor';
import { AppSettings } from 'src/app/configs/app.settings';
import { getCurrencySymbol } from '@angular/common';
import { Entity } from 'src/app/core/data/schema/entity';

export class Raffle {

    static currentRaffle: Raffle;
    static className = 'Raffle';

    raffleId: number;
    userId: number;
    name: string;
    description: string;
    saleStart: string;
    saleEnd: string;
    logoPath: string;
    bannerPath: string;
    licenseNumber: string;
    licensePath: string;
    drawType: RaffleDrawType;
    country: string;
    province: string;
    minAge: number;
    organizationName: string;
    contactName: string;
    contactEmail: string;
    lastModified: string;
    fee: number | null;
    status: RaffleStatus;
    accessCode: string;
    createdOn: string;
    lastModOn: string | null;
    isPrivate: boolean;
    showSellerLeaderboard: boolean;
    requireSellerSelection: boolean;
    showAmountRaised: boolean;
    auctionAccessCode: string;
    rules: string;
    organizationWebsite: string;
    prizeClaimInstructions: string;
    totalValue: number;
    raffleType: RaffleType;
    raffleLocation: string;
    linkFb: string;
    linkLinkedin: string;
    linkYoutube: string;
    linkTwitter: string;
    linkInstagram: string;
    brandingColor: string;
    prizes: Prize[] = [];
    sponsors: Sponsor[];
    winners: RaffleWinner[];
    pricingTiers: PricingTier[];
    totalRaised: number;
    ticketsSold: number;
    currency = '';
    remaining: string;
    currencySymbol = '$';
    startObj: Date;
    endObj: Date;

    private remainingObj: any = {};
    private startFmt: string;
    private endFmt: string;

    public constructor(init?: Partial<Raffle>) {
        Object.assign(this, init);
        if (this.prizes != null) {
            this.prizes = this.prizes.map(m => new Prize(m));
            // sort by sort order
            this.prizes.sort((a, b) => a.sortOrder - b.sortOrder);
        }
        if (this.pricingTiers != null) {
            this.pricingTiers = this.pricingTiers.sort((a, b) => a.price - b.price);

            this.pricingTiers = this.pricingTiers.map(m => new PricingTier(m));

            // filter out $0 tickets, only to be used for manual orders
            this.pricingTiers = this.pricingTiers.filter(t => t.price > 0);
        }

        this.currency = this.currency.toUpperCase();
        this.currencySymbol = getCurrencySymbol(this.currency, 'narrow');

        this.refreshDisplayDates();
        this.setTimeRemaining();
        this.startTimeRemainingInterval();
    }

    get license() {
        if (this.licenseNumber) {
            return this.licenseNumber;
        } else {
            return 'Not provided';
        }
    }

    get winnerTakes() {
        return this.totalRaised / 2;
    }
    get startDisplay() { return this.startFmt; }
    get endDisplay() { return this.endFmt; }
    get hasStarted() { return (this.status === RaffleStatus.Active && new Date(this.saleStart).valueOf() <= new Date().valueOf()); }
    get hasEnded() { return (new Date(this.saleEnd).valueOf() <= new Date().valueOf()); }
    get isDrawn() { return this.status === RaffleStatus.Drawn; }
    get isPurchaseAvailable() {
        return (this.hasEnded === false && this.hasStarted === true);
    }
    get isLive() { return (this.hasStarted === true && this.hasEnded === false); }
    get timeRemaining() { return this.remaining; }
    get timeRemainingObj() { return this.remainingObj; }

    get shareUrl() {
        return `${AppSettings.clientUrl}/raffle.php?code=${this.accessCode}`;
    }
    get hasBanner() {
        if (this.bannerPath) {
            return true;
        } else {
            return false;
        }
    }

    static async fetchPublic() {
        return await Entity.factory.fetchObj(Raffle, 'raffleguest', 'public', 'json', true);
    }

    static async getByCode(code: string, setCurrent: boolean = true) {
        const raffle = await Entity.factory.getObj(Raffle, 'raffleguest', `code/${code}`, 'json', true);
        if (raffle != null && setCurrent) {
            this.currentRaffle = raffle;
        }
        return raffle;
    }

    lastDrawDate() {
        if (this.prizes.length === 0) {
            return 'Not set';
        }

        const clonedArray = [...this.prizes];
        return clonedArray.sort((a, b) => new Date(b.drawDate).getTime() - new Date(a.drawDate).getTime())[0].drawFmt;
    }

    private refreshDisplayDates() {
        if (this.saleStart != null && this.startObj == null) {
            this.saleStart = this.saleStart + 'Z';
            this.startObj = new Date(this.saleStart);
            this.startFmt = DateTimeHelper.formatDate(this.saleStart);
        }
        if (this.saleEnd != null && this.endObj == null) {
            this.saleEnd = this.saleEnd + 'Z';
            this.endObj = new Date(this.saleEnd);
            this.endFmt = DateTimeHelper.formatDate(this.saleEnd);
        }
    }

    private startTimeRemainingInterval() {
        setInterval(() => {
            this.setTimeRemaining();
        }, 1000);
    }

    private setTimeRemaining() {
        if (this.hasEnded === true) {
            this.remaining = 'Raffle has Ended';
        } else if (this.hasStarted === false) {
            const remaining = DateTimeHelper.getTimeRemaining(undefined, new Date(this.saleStart).valueOf());
            this.remainingObj = remaining.parts;
            this.remaining = `${remaining.clock}`;
        } else {
            const remaining = DateTimeHelper.getTimeRemaining(new Date(this.saleStart).valueOf(), new Date(this.saleEnd).valueOf());
            this.remainingObj = remaining.parts;
            this.remaining = `${remaining.clock}`;
        }
    }
}


export class RaffleTicketPurchase {
    raffleId: number;
    tier: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;

    public constructor(init?: Partial<RaffleTicketPurchase>) {
        Object.assign(this, init);
    }
}

export class RaffleTicketPayment {
    purchaseGuid: string;
    token: string;
    amountPaid: number;
    description: string;

    public constructor(init?: Partial<RaffleTicketPayment>) {
        Object.assign(this, init);
    }
}


